import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Messaging, getToken, onMessage } from '@angular/fire/messaging';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class FirebaseMessagingService {
  private messageSubject = new BehaviorSubject<any>(null);
  private orderCancelledSubject = new Subject<any>();
  private rechargeSubject = new Subject<any>();
  private statusReclaimSubject = new Subject<any>();
  private reassignSubject = new Subject<any>();
  private apiUrl = environment.api_baseUrl;
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) platformId: Object,
    private messaging: Messaging,
    private http: HttpClient,
    private authService: AuthService
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      this.setupForegroundMessageListener();
    }
  }

  private setupForegroundMessageListener() {
    if (!this.isBrowser) {
      return;
    }
    onMessage(this.messaging, (payload) => {
      // console.log('Mensaje recibido en primer plano:', payload);
      this.messageSubject.next(payload);

      // Verificar si es una notificación de cancelación de orden
      const title = payload.notification?.title || payload.data?.['title'] || '';
      if (title === 'Se ha cancelado la orden') {
        // console.log('Notificación de cancelación de orden detectada');
        this.orderCancelledSubject.next(payload);
      }

      // Verificar si es una notificación de cambio de estado de reclamo
      if (title.includes('Incidencia de pedido actualizada')) {
        // console.log('Notificación de cambio de estado de reclamo detectada');
        this.statusReclaimSubject.next(payload);
      }

      // Verificar si es una notificación de recarga
      if (title.includes('Penalización por la orden')) {
        // console.log('Notificación de recarga detectada');
        this.rechargeSubject.next(payload);
      }

      if (title.includes('Su pedido fue reasignado')) {
        // console.log('Notificación de reasignación de orden detectada');
        this.reassignSubject.next(payload);
      }
      // Mostrar notificación personalizada en primer plano
      this.showForegroundNotification(payload);
    });
  }

  private showForegroundNotification(payload: any) {
    if (!this.isBrowser) {
      return;
    }
    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
      const title = payload.notification?.title || payload.data?.['title'] || 'Nueva notificación';
      const options = {
        body: payload.notification?.body || payload.data?.['body'] || 'Tienes una nueva notificación',
        icon: payload.notification?.icon || payload.data?.['icon'] || '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png',
        tag: 'foreground-notification',
        data: payload.data || {}
      };

      const notification = new Notification(title, options);

      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      // Auto cerrar después de 5 segundos
      setTimeout(() => {
        notification.close();
      }, 5000);
    }
  }

  getMessages(): Observable<any> {
    return this.messageSubject.asObservable();
  }

  getOrderCancelledNotifications(): Observable<any> {
    return this.orderCancelledSubject.asObservable();
  }

  getRechargeNotifications(): Observable<any> {
    return this.rechargeSubject.asObservable();
  }

  getStatusReclaimNotifications(): Observable<any> {
    return this.statusReclaimSubject.asObservable();
  }

  getReassignNotifications(): Observable<any> {
    return this.reassignSubject.asObservable();
  }

  sendTokenToServer(fcmToken: string): Observable<any> {
    const bearerToken = 'Bearer ' + this.authService.getToken();
    const headers = {
      'Authorization': bearerToken,
      'Content-Type': 'application/json'
    };
    const body = {
      token: fcmToken,
      device_type: 'web',

    };
    return this.http.post(this.apiUrl + '/api/notification/device/register', body, { headers });
  }
  async installFVMServiceWorker() {
    try {
      if (!this.isBrowser) {
        return;
      }
      // Verificar si ya existe un service worker de Firebase
      const registrations = await navigator.serviceWorker.getRegistrations();
      let firebaseRegistration = registrations.find(
        (reg) => reg.active && reg.active.scriptURL.includes('firebase-messaging')
      );

      if (firebaseRegistration) {
        // console.log('Firebase Service Worker ya está registrado');
        await this.requestNotificationPermission(firebaseRegistration);
      } else {
        // console.log('Registrando Firebase Service Worker');
        const newRegistration = await this.registerSw();
        if (newRegistration) {
          await this.requestNotificationPermission(newRegistration);
        }
      }
    } catch (error) {
      console.error('Error en installFVMServiceWorker:', error);
    }
  }

  private async registerSw(): Promise<ServiceWorkerRegistration | null> {
    try {
      if (!this.isBrowser) {
        return null;
      }
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      // console.log('Firebase Service Worker registrado exitosamente');
      return registration;
    } catch (error) {
      console.error('Error registering Firebase SW:', error);
      return null;
    }
  }

  private async requestNotificationPermission(registration: ServiceWorkerRegistration) {
    try {
      if (!this.isBrowser) {
        return;
      }
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        // console.log('Permiso de notificaciones concedido');
        // Obtener el token FCM
        const token = await getToken(this.messaging, {
          vapidKey: environment.vapidKey,
          serviceWorkerRegistration: registration
        });
        if (token) {
          // console.log('Token FCM obtenido:', token);
          // Enviar token al servidor
          this.sendTokenToServer(token).subscribe({
            next: (response) => {
              //console.log('Token enviado al servidor:', response)
            },
            error: (error) => console.error('Error al enviar token:', error)
          });
        }
      } else {
        // console.log('Permiso de notificaciones denegado');
      }
    } catch (error) {
      console.error('Error al solicitar permisos de notificación:', error);
    }
  }
}

